import { $Q } from '../utils/query-selector';
import { dataToggle } from '../utils/toggle-dataset';
import axios from 'axios';
const { iconClose, iconCaret } = window.icons;

const dateFormat = (data) =>
  `${new Date(data.timestamp).getDate()} ${
    window.months[new Date(data.timestamp).getMonth()]
  }`;

const caretComponent = (cssClass) => `
  <button
    type="button"
    class="js-${cssClass} ${cssClass}"
  >
    ${iconCaret}
  </button>
`;

const captionComponent = (data) => {
  return `
  <div class="post-popup__caption">
    <div class="post-popup__caption-description">
      ${data.trim()}
    </div>
  </div>
`;
};

const dateComponent = (data) => `
  <div class="post-popup__footer-wrapper">
    <hr>
    <div class="post-popup__footer">
      <span class="post-popup__date">
        ${dateFormat(data)}
      </span>

      <a href="${data.permalink}" target="_blank" class="post-popup__link">
        ${window.instagramComponent.footerText}
      </a>
    </div>
  </div>
`;

function mediaComponent(data) {
  if (data.media_type === 'VIDEO') {
    return `
      <video width="400" controls="controls" preload="metadata">
        <source src="${data.media_url}" type="video/mp4">
      </video>
    `;
  } else {
    return `
      <img
        src="${data.media_url}"
        alt="Instagram image"
      />
    `;
  }
}

function popupStructure(data) {
  return `
    <div class="post-popup__wrapper">
      <div class="post-popup__image">
        ${mediaComponent(data)}
      </div>

      <div class="post-popup__content">
        <div class="post-popup__header">
          <img
            src="${
              data.media_type === 'VIDEO' ? data.thumbnail_url : data.media_url
            }"
            alt="${'Image alt'}"
          />

          <div class="post-popup__profile">
            <a href="${data.link}" target="_blank">${data.username}</a>
            <span>@${data.username}</span>
          </div>

          <button
            type="button"
            class="post-popup__close js-post-popup-close"
          >
            ${iconClose}
          </button>
        </div>

        <hr>

        <div class="post-popup__controls">
          ${caretComponent('prev')}
          ${caretComponent('next')}
        </div>

        ${captionComponent(data.caption)}
        ${dateComponent(data)}
      </div>
    </div>
  `;
}

/**
 * Call dataToggle function to close popup
 * @param {node} popup - Popup node
 * @returns null - stop called
 * @author Daniel Santos
 */
function closePopup(popup) {
  const closeSelector = $Q('.js-post-popup-close');

  if (!closeSelector) return;

  closeSelector.addEventListener('click', () => {
    dataToggle(popup, true);
  });
}

/**
 * Move with the arrows into Popup
 * @param {node} node - Node arrow
 * @param {number} direction - Index to indicate direction slider
 * @param {node} post Next or previous post
 * @param {function} cbArrows - Call function of the arrows to active click
 * @see popupArrows
 * @returns null - stop called
 * @author Daniel Santos
 */
function movePopupArrows({ node, direction, post, cbArrows, currentPost }) {
  const popup = $Q('#js-instagram-post-popup');

  // To responsive, block post after post number 3
  if (window.matchMedia('(max-width: 749px)').matches && direction >= 4) return;

  if (!node) return;

  if (!post) {
    node.setAttribute('disabled', true);
  }

  node.addEventListener('click', async () => {
    if (!post) return;
    popup.innerHTML = '<div class="loading"></div>';

    let postDetail = post.dataset.post;

    if (!postDetail) {
      postDetail = await getInstagramDetailData(post.id);
      post.dataset.post = JSON.stringify(postDetail);
    } else {
      postDetail = JSON.parse(post.dataset.post);
    }

    popup.innerHTML = popupStructure(postDetail);

    closePopup(popup);
    cbArrows(direction);
  });
}

/**
 * Call movePopupArrows to change info of the next slide
 * Get data of the next post with current position post
 * @param {number} index - post position
 * @returns null - stop called
 * @author Daniel Santos
 */
function popupArrows(index) {
  const next = $Q('.js-next');
  const prev = $Q('.js-prev');
  const currentPost = $Q(`.js-instagram-post-${index}`);

  movePopupArrows({
    node: next,
    direction: index + 1,
    post: currentPost.nextSibling,
    cbArrows: popupArrows,
    currentPost,
  });
  movePopupArrows({
    node: prev,
    direction: index - 1,
    post: currentPost.previousSibling,
    cbArrows: popupArrows,
    currentPost,
  });
}

/**
 * Fill popup with instagram data
 * @param {node} node - Popup node
 * @param {string} data
 * @returns null - stop called
 */
function fillPopup(node, postDetail, index, open) {
  if (!node) return;

  const popup = node;

  if (open) {
    popup.innerHTML = popupStructure(postDetail);

    popupArrows(index);
  }
}

/**
 * Call function fillPopup and closePopup with a instagram card image
 * @param {node} control - Instagram card image node and trigger of modal
 * @author Daniel Santos
 * @see fillPopup
 * @see closePopup
 */
function togglePopup(control, index) {
  const imageControl = $Q(control);
  const popup = $Q('#js-instagram-post-popup');

  imageControl.addEventListener('click', async () => {
    const open = dataToggle(popup, true);
    popup.innerHTML = '<div class="loading"></div>';
    let postDetail = imageControl.dataset.post;

    if (!postDetail) {
      postDetail = await getInstagramDetailData(imageControl.id);
      imageControl.dataset.post = JSON.stringify(postDetail);
    } else {
      postDetail = JSON.parse(imageControl.dataset.post);
    }

    fillPopup(popup, postDetail, index, open);
    closePopup(popup);
  });
}

/**
 * Create instagran post component
 * Each component will have a data-set with detail data about post
 * With this data hydrated modal
 * @param {object} postData - Post data
 * @param {number} index - For each index
 * @returns
 */
function postComponent(post, index) {
  const containerPost = document.createElement('div');
  containerPost.setAttribute(
    'class',
    `instagram__post js-instagram-post-${index + 1}`
  );
  containerPost.id = post.id;
  let imageUrl = post.media_url;

  if (post.media_type === 'VIDEO') imageUrl = post.thumbnail_url;

  containerPost.innerHTML = `
    <img
      src='${imageUrl}'
      alt='Instagram image'
      title='Instagram image'
      class='instagram__post-image'
    />
  `;

  return containerPost;
}

async function getInstagramDetailData(id) {
  const { data } = await axios(`${window.vinteApi}/instagram/posts/${id}`);

  return data;
}

async function getInstagramData() {
  const { data } = await axios(`${window.vinteApi}/instagram/last-posts`);

  return data;
}

function renderTitle(name) {
  const nodeName = $Q('#js-instagram-name');

  if (nodeName) nodeName.innerHTML = `@${name}`;
}

/**
 * Render post image and call togglePopup
 * Render title vintega account
 * Validate screen width to render 3 or 6 posts
 * @see togglePopup
 * @author Daniel Santos
 */
export async function instagramPopup() {
  const instagramPostsContainer = $Q('#js-instagram-posts');

  if (!instagramPostsContainer) return;

  const loader = $Q('.js-instagram .loading');
  const { data, username } = await getInstagramData();

  renderTitle(username);

  for (let index = 0; index < data.length; index++) {
    const post = data[index];
    const mql = window.matchMedia('(max-width: 749px)');

    instagramPostsContainer.appendChild(postComponent(post, index));
    if (loader) loader.remove();
    togglePopup(`.js-instagram-post-${index + 1}`, index + 1);

    if (mql.matches) {
      if (index === 2) break;
    } else {
      if (index === 5) break;
    }
  }
}
